import { physicalKey } from './keyboard';

const MOVE_KEYS = new Set([
  'w', 'a', 's', 'd',
  'arrowup', 'arrowdown', 'arrowleft', 'arrowright',
]);

/**
 * Keyboard input as a normalized movement intent vector + a queue of one-shot actions.
 * In world coordinates y increases downward, so "up" decreases y.
 *
 * The number keys (1–4) are deliberately NOT handled here: they drive the action-bar interaction menus
 * (open a menu → type an item number) in the HUD — see features/office/useInteractionMenus. Emotes are
 * still sent over the same realtime-client `sendEmote` path, but they're triggered by PICKING from the
 * Animations menu now, never by a raw number key.
 */
/** Discrete one-shot actions triggered by a key press (not held). */
export type InputAction = 'give-reputation';

/**
 * True when the event target is a text-entry control (form field or contentEditable), so global
 * gameplay keys (WASD/arrows/G) don't fire while the player is typing — e.g. operating the mic device
 * `<select>`. Mirrors the V/E listener guard in OfficePage.
 */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export class KeyboardInput {
  private readonly pressed = new Set<string>();
  private readonly actionQueue: InputAction[] = [];

  private readonly onKeyDown = (e: KeyboardEvent) => {
    // Don't steal keystrokes while the player is typing in a form field / contentEditable.
    if (isTyping(e.target)) return;
    // Resolve by PHYSICAL key position, not the produced character, so WASD/emotes/G work on every
    // keyboard layout (Russian, AZERTY, …) — see physicalKey().
    const key = physicalKey(e);
    if (MOVE_KEYS.has(key)) {
      this.pressed.add(key);
      e.preventDefault();
    } else if (key === 'g' && !e.repeat) {
      // Give reputation to the nearest teammate (the philosophy's core social gesture).
      this.actionQueue.push('give-reputation');
    }
    // Note: the E "interact" key is handled at the WINDOW level (OfficePage), routed through the
    // engine's interactNearest(), so the in-world keypress and the accessible HUD button share one
    // path (no double-send). See M4. The number keys (1–4) are handled by the HUD's interaction-menu
    // controller (useInteractionMenus), not here.
  };

  private readonly onKeyUp = (e: KeyboardEvent) => {
    // Same resolution as keydown, or a held key released on a non-US layout would never clear.
    this.pressed.delete(physicalKey(e));
  };

  attach(): void {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.pressed.clear();
    this.actionQueue.length = 0;
  }

  /** Current movement intent. Each axis is -1, 0, or 1 (the server normalizes diagonals). */
  vector(): { x: number; y: number } {
    let x = 0;
    let y = 0;
    if (this.pressed.has('a') || this.pressed.has('arrowleft')) x -= 1;
    if (this.pressed.has('d') || this.pressed.has('arrowright')) x += 1;
    if (this.pressed.has('w') || this.pressed.has('arrowup')) y -= 1;
    if (this.pressed.has('s') || this.pressed.has('arrowdown')) y += 1;
    return { x, y };
  }

  /** Pop the next queued one-shot action, if any. */
  consumeAction(): InputAction | null {
    return this.actionQueue.shift() ?? null;
  }
}
